import { isAxiosError } from "./client";
import { TrainerInviteConflictError } from "@membentry/shared-types";
/**
 * Extract the conflicting user from a 409 invite response, tolerating both a
 * raw `UserSearchResultDTO` body and an `ApiResponse<UserSearchResultDTO>`
 * envelope (payload). Keeps conflict extraction robust to whatever shape the
 * backend uses for the 409 body.
 */
const extractConflictUser = (data) => {
    if (data != null &&
        typeof data === "object" &&
        "payload" in data &&
        data.payload != null) {
        return data.payload;
    }
    return data;
};
// ─── Club-Scoped Trainer Endpoints ───────────────────────────────
export const getTrainers = async (client, clubId, params) => {
    const response = await client.get(`/clubs/${clubId}/trainers`, { params });
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const searchClubUsers = async (client, clubId, query) => {
    const response = await client.get(`/clubs/${clubId}/users/search`, { params: { query } });
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data.payload ?? [];
};
export const promoteTrainer = async (client, clubId, body) => {
    const response = await client.post(`/clubs/${clubId}/trainers/promote`, body);
    if (response.data?.payload == null) {
        throw new Error("No data received from the server");
    }
    return response.data.payload;
};
export const inviteTrainer = async (client, clubId, body) => {
    try {
        const response = await client.post(`/clubs/${clubId}/trainers/invite`, body);
        if (response.data?.payload == null) {
            throw new Error("No data received from the server");
        }
        return response.data.payload;
    }
    catch (error) {
        if (isAxiosError(error) && error.response?.status === 409) {
            const conflictUser = extractConflictUser(error.response.data);
            throw new TrainerInviteConflictError(conflictUser);
        }
        throw error;
    }
};
export const deleteTrainer = async (client, clubId, trainerId) => {
    await client.delete(`/clubs/${clubId}/trainers/${trainerId}`);
};
// ─── Public Trainer Profile Endpoints ───────────────────────────
export const getClubTrainersPublic = async (client, clubId) => {
    const response = await client.get(`/clubs/${clubId}/trainers/public`);
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const getMyTrainerProfile = async (client, clubId) => {
    const response = await client.get(`/clubs/${clubId}/trainers/me`);
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const updateTrainerProfile = async (client, clubId, trainerId, body) => {
    const response = await client.patch(`/clubs/${clubId}/trainers/${trainerId}/profile`, body);
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
/**
 * @deprecated Use `inviteTrainer` or `promoteTrainer` instead.
 * This function is kept for backward compatibility with legacy code.
 */
export const createTrainer = async (client, data) => {
    const response = await client.post("/trainers", data);
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
